import { CSSProperties, ReactNode, useCallback, useRef, useState } from 'react';
import { Text, TextProps } from '@mantine/core';

import { Tooltip, TooltipProps } from './Tooltip';

/** Ignore subpixel / line-height rounding (often ~1–2px) so short labels don't enable the tooltip. */
const OVERFLOW_HEIGHT_TOLERANCE_PX = 2;

type TextWithTooltipProps = {
  // children to be wrapped by tooltip component
  children: ReactNode;
  // Tooltip props, see custom Tooltip component
  tooltipProps?: Partial<TooltipProps>;
  // Text props for MantineUI Text component, see https://mantine.dev/core/text/?t=props
  textProps?: TextProps;
  // Tooltip is permanently shown
  style?: CSSProperties;
};

// appends a tooltip to children node
// Use textProps lineClamp to show a tooltip for clamped text only!
export function TextWithTooltip({
  children,
  tooltipProps,
  textProps,
  style,
}: TextWithTooltipProps) {
  const [disabled, setDisabled] = useState(true);
  const ref = useRef<HTMLParagraphElement>(null);

  const updateDisabledFromOverflow = useCallback(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const hasVerticalOverflow =
      el.scrollHeight - el.clientHeight > OVERFLOW_HEIGHT_TOLERANCE_PX;
    const hasHorizontalOverflow =
      el.scrollWidth - el.clientWidth > OVERFLOW_HEIGHT_TOLERANCE_PX;
    setDisabled(!(hasVerticalOverflow || hasHorizontalOverflow));
  }, []);

  return (
    <Tooltip
      label={tooltipProps?.label ?? children}
      {...tooltipProps}
      disabled={tooltipProps?.disabled ?? disabled}
    >
      <Text
        inherit
        {...textProps}
        ref={ref}
        onMouseEnter={updateDisabledFromOverflow}
        style={style}
      >
        {children}
      </Text>
    </Tooltip>
  );
}
